"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.saveLineItemMoneyRequestSchema = exports.saveOccurrenceMoneyRequestSchema = exports.occurrenceEconomicsSchema = exports.lineItemEconomicsSchema = exports.lineItemMoneySchema = exports.MAN_DAY_HOURS = exports.LINE_ITEM_SCOPES = void 0;
exports.perHour = perHour;
exports.occurrenceSpan = occurrenceSpan;
exports.lineItemTotal = lineItemTotal;
exports.perPeriodOf = perPeriodOf;
exports.revenueOf = revenueOf;
exports.budgetOf = budgetOf;
exports.invoicedPeriodStarts = invoicedPeriodStarts;
exports.lineItemInPeriod = lineItemInPeriod;
exports.lineItemEarnsIn = lineItemEarnsIn;
exports.invoicedByPeriod = invoicedByPeriod;
exports.marginPct = marginPct;
const zod_1 = require("zod");
const contract_1 = require("./contract");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Line-item economics — C55, Phase 4. Budget, revenue, cost, margin.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THREE FIGURES, AND ONLY TWO OF THEM ARE STORED.** *Budget* and *revenue* are commercial
 * facts somebody agreed and typed; *cost* is arithmetic over logged hours and the people who logged
 * them. Storing cost would be a number that was true on the day it was written — and this product's
 * whole finance layer exists to answer *is it still true*.
 *
 * ⚠️⚠️ **EVERY RULE HERE BRANCHES ON THE LINE ITEM'S TYPE, AND THEY ARE GENUINELY DIFFERENT
 * CONTRACTS.** A `fixed` deliverable earns what was agreed whatever it takes; a
 * `time_and_material` one earns what was worked and has no budget at all; a `non_billable` one
 * earns nothing and still costs. A single formula over the three would be wrong for at least two.
 *
 * ⚠️⚠️ **C55 ADDS A FOURTH AND IT IS THE ODD ONE OUT: `license` EARNS WITH NO HOURS BEHIND
 * IT AT ALL.** *"Nincs óra és nincs allokáció."* So it has no budget, no estimate and no scope,
 * and its cost side is empty by construction. ⚠️ **U-7 · its income is its `revenue` and nothing
 * else** — the quantity, unit and unit price it was once priced by are gone (`0212`).
 *
 * ┌──────────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ U-5 · ON A RECURRING CONTRACT THE STORED MONEY IS PER PERIOD.            │
 * └──────────────────────────────────────────────────────────────────────────────┘
 *
 * `budget` and `revenue` are typed and stored per period, as `estimate_hours` always was (P2-11);
 * off a recurring contract they are the whole agreement. So a stored figure is the money of ONE
 * unit — one period, or the whole thing — and every helper here takes `periods`, **how many units
 * the answer covers**: `1` for one period (or for anything off a recurring contract), and
 * `lineItemMoneyPeriods()` for a recurring line item's whole span. There is no default, so every
 * caller says which figure it wants. This replaces P2-33.2's *a total divided by the span*.
 */
exports.LINE_ITEM_SCOPES = ['retainer', 'billable'];
/**
 * ⚠️ **The hours in a man-day, for turning a daily rate into an hourly one.** The mockup divides by
 * a flat eight (`r.unit === 'day' ? v / 8 : v`) and so does this: a *day rate* is a commercial unit
 * agreed with a client, not a claim about anybody's schedule, and reading it against the person's
 * actual working day would make the same contract worth different money to a part-timer.
 */
exports.MAN_DAY_HOURS = 8;
/** A rate expressed per hour, whatever unit it was agreed in. */
function perHour(rate, unit) {
    return unit === 'day' ? rate / exports.MAN_DAY_HOURS : rate;
}
/**
 * The money on a line item, as the wire carries it.
 *
 * ⚠️ **Every figure is nullable and null is NOT zero.** A time-and-materials deliverable HAS no
 * budget — a different statement from having a budget of nothing, and the screen draws the two
 * differently: an em-dash against a `0 Ft`.
 */
exports.lineItemMoneySchema = zod_1.z.object({
    scope: zod_1.z.enum(exports.LINE_ITEM_SCOPES),
    budget: zod_1.z.string().nullable(),
    revenue: zod_1.z.string().nullable(),
    rate: zod_1.z.string().nullable(),
    rateUnit: zod_1.z.enum(['hour', 'day']).nullable(),
    currencyCode: zod_1.z.string().nullable(),
});
/**
 * ⚠️ **A span floored at one.** A recurring line item whose window overlaps no period still has its
 * stored figure, and multiplying it by zero would make an agreement vanish.
 */
function occurrenceSpan(overlapping) {
    return Math.max(1, overlapping);
}
/**
 * U-5 · a stored figure's total over `periods` units — per period × the span on a recurring
 * contract, the figure itself (`periods` = 1) anywhere else.
 */
function lineItemTotal(stored, periods) {
    return stored * occurrenceSpan(periods);
}
/**
 * U-5 · the per-period figure behind a total — for a screen that holds a total and must type
 * the per-period one. The inverse of `lineItemTotal`.
 */
function perPeriodOf(total, periods) {
    return total / occurrenceSpan(periods);
}
/**
 * What a line item EARNS in a window — the whole thing when no window is given.
 *
 * ⚠️⚠️ **`time_and_material` IS THE ONLY TYPE WHOSE REVENUE IS A FACT RATHER THAN AN AGREEMENT.**
 * It is the hours actually logged against it at the agreed rate, so it moves every day; the other
 * two are what somebody signed. Passing `loggedHours` for a fixed item is harmless and ignored,
 * which is deliberate: the caller sums a list and should not have to branch first.
 */
function revenueOf(input) {
    if (input.type === 'non_billable')
        return 0;
    if (input.type === 'time_and_material') {
        const rate = Number(input.money.rate ?? '0');
        if (rate <= 0 || input.money.rateUnit === null)
            return 0;
        return input.loggedHours * perHour(rate, input.money.rateUnit);
    }
    /* ⚠️ U-7 · a licence earns its `revenue`, like a fixed item — the unit pricing is gone. */
    return lineItemTotal(Number(input.money.revenue ?? '0'), input.periods);
}
/**
 * What a line item's frame IS.
 *
 * ⚠️ **A time-and-materials item has none, and `0` would be a lie.** It is not over budget the
 * moment an hour is logged; it simply is not the kind of thing that has one. `null` is what the
 * screen draws as an em-dash and what a sum has to skip rather than add.
 */
function budgetOf(input) {
    if (input.type === 'time_and_material')
        return null;
    /* ⚠️ C55 · a licence has no budget either — `0151` holds it as a CHECK, and `null` is the
       em-dash the column draws rather than a `0 Ft` nobody agreed. */
    if (input.type === 'license')
        return null;
    if (input.money.budget === null)
        return null;
    return lineItemTotal(Number(input.money.budget), input.periods);
}
/**
 * ┌──────────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ U-6 · A RECURRING CONTRACT IS INVOICED BY PERIOD.                        │
 * └──────────────────────────────────────────────────────────────────────────────┘
 *
 * Its line items cannot be invoiced one by one, so what a line item HAS invoiced is derived:
 * its income in each period of its own span that carries an invoice, summed. *"If the line item
 * is 2 million over 10 periods, that is 200k per period; if 4 are billed, 800k is billed."*
 *
 * ⚠️⚠️ **A PERIOD IS INVOICED BY ANY NON-VOID PERIOD INVOICE, OF ANY AMOUNT.** A draft counts —
 * the rule every other sum in C57 keeps (*Piszkozat* is money we intend to ask for). Only an
 * invoice whose target IS the period marks it; a line-item invoice does not.
 *
 * ⚠️⚠️ **LEGACY LINE-ITEM INVOICES ARE ADDED AT THEIR AMOUNT, ONCE.** Invoices raised against a
 * recurring line item before U-6 stay (read-only). They count on their own line item at what they
 * say, and they do NOT mark the period they were issued in — or that line item would be counted
 * twice for the same month: once as the period's share and once as the invoice.
 */
function invoicedPeriodStarts(invoices) {
    const starts = new Set();
    for (const one of invoices) {
        if (one.status === 'void' || one.targetKind !== 'occurrence')
            continue;
        if (one.occurrenceStartsOn !== null)
            starts.add(one.occurrenceStartsOn);
    }
    return starts;
}
/** Whether a line item's own window (each end defaulting to the period's) overlaps a period. */
function lineItemInPeriod(item, period) {
    const from = item.startsOn ?? period.from;
    const to = item.endsOn ?? period.to;
    return period.to >= from && period.from <= to;
}
/**
 * 21 Oct 2026 · whether a line item EARNS in a period: when the two overlap, as above — or, for
 * time and material, whenever hours were logged on it inside the period, whatever its own dates
 * say. The product owner: hours, cost and revenue all count on T&M; an hour outside the line
 * item's dates is raised as `wlog_outside_li`, not dropped. ⚠️ Safe for budgets: a T&M line item
 * has none (`budgetOf`), so widening which periods it belongs to only adds what it earned.
 */
function lineItemEarnsIn(item, period, hoursInPeriod) {
    return lineItemInPeriod(item, period) || (item.type === 'time_and_material' && hoursInPeriod > 0);
}
/**
 * U-6 · what a recurring line item has invoiced: its income in every invoiced period of its own
 * span, plus the legacy line-item invoices on it.
 *
 * @param periods the contract's series — already narrowed to a window when the caller has one.
 *   Only the periods the item overlaps are read.
 * @param incomeIn the item's income in one period: its stored per-period revenue for a fixed or
 *   licence item, the hours logged inside the period × the rate for a T&M one, nothing for a
 *   non-billable one — `revenueOf` with `periods: 1`.
 */
function invoicedByPeriod(input) {
    let total = input.legacy;
    for (const period of input.periods) {
        if (!input.invoiced.has(period.from) || !lineItemInPeriod(input.item, period))
            continue;
        total += input.incomeIn(period);
    }
    return total;
}
/**
 * ⚠️⚠️ **THE MARGIN IS AGAINST REVENUE, NOT AGAINST COST — AND THE DENOMINATOR IS THE WHOLE
 * ARGUMENT.** `(revenue − cost) / revenue` is the share of what we charged that we keep, which is
 * what a delivery business means by margin; `/ cost` would be markup, a different number that looks
 * plausible and is roughly double at healthy rates.
 *
 * ⚠️ `null` where there is no revenue: a non-billable deliverable has no margin, and dividing by
 * zero to print `-Infinity%` is worse than saying so.
 */
function marginPct(revenue, cost) {
    if (revenue <= 0.004)
        return null;
    return Math.round(((revenue - cost) / revenue) * 100);
}
/**
 * The finance block a line item's detail draws, and the shape every caller of the economics gets.
 *
 * ⚠️ **`invoiced` and `remaining` are C57's, and `remaining` is floored at zero.** Billing ahead of
 * delivery is ordinary — a retainer invoiced on the first of the month has earned nothing yet — so
 * a negative figure here would read as a refund owed rather than as *nothing more to invoice*. The
 * over-billed case is told by `invoiced` exceeding `revenue`, which the screen draws in the
 * destructive colour.
 */
exports.lineItemEconomicsSchema = zod_1.z.object({
    lineItemId: zod_1.z.string(),
    /* ⚠️⚠️ **`LINE_ITEM_TYPES`, NOT A COPY OF ITS MEMBERS.** Three schemas each inlined the
       same three strings, so C55's `license` typechecked in `contract.ts` and silently
       disagreed here — caught by the compiler only because the API assigns between them. */
    type: zod_1.z.enum(contract_1.LINE_ITEM_TYPES),
    scope: zod_1.z.enum(exports.LINE_ITEM_SCOPES),
    currencyCode: zod_1.z.string().nullable(),
    /**
     * U-5 · **the TOTAL** — on a recurring contract the stored per-period figure × `periods`.
     * `revenue` below is the same: the whole agreement (a T&M item's: what its hours earned).
     */
    budget: zod_1.z.string().nullable(),
    /**
     * ⚠️ U-5 · **added under rule 8: `periods`, `budgetPerPeriod`, `revenuePerPeriod`.** How many
     * periods the totals cover (`lineItemMoneyPeriods` — 1 off a recurring contract), and the two
     * STORED figures, which are what the money card edits. Without them the card would have to
     * divide a total back, and a rounded division is a figure nobody typed.
     */
    periods: zod_1.z.number().int().positive(),
    budgetPerPeriod: zod_1.z.string().nullable(),
    revenuePerPeriod: zod_1.z.string().nullable(),
    /**
     * ⚠️ **The AGREED rate on a time-and-material deliverable, and what it is charged per.** It is
     * the input the revenue above is computed FROM, so a screen that lets somebody price the item
     * needs it back — the read carried the answer and not the question, which left the rate settable
     * by nothing. Null on every other type.
     */
    rate: zod_1.z.string().nullable(),
    rateUnit: zod_1.z.enum(['hour', 'day']).nullable(),
    revenue: zod_1.z.string(),
    /** Logged hours × each person's own cost rate, converted into this line item's currency. */
    cost: zod_1.z.string(),
    /** ⚠️ Null where there is no revenue to take a share of — see `marginPct`. */
    marginPct: zod_1.z.number().int().nullable(),
    /**
     * ⚠️ Every non-`void` invoice against this line item, in its currency. A draft counts.
     * ⚠️ U-6 · on a recurring contract it is `invoicedByPeriod` instead: the per-period income of
     * every invoiced period of its span, plus any legacy line-item invoice on it.
     */
    invoiced: zod_1.z.string(),
    remaining: zod_1.z.string(),
    /** The hours behind the figures, so a screen can show the two together. */
    loggedHours: zod_1.z.string(),
    plannedHours: zod_1.z.string(),
    /**
     * ⚠️ **People whose cost could NOT be resolved**, because they have no rate card in force. Their
     * hours are in `loggedHours` and their money is in nothing — so a cost figure that looked
     * complete would be understated with no way to tell. The screen says how many.
     */
    unratedMembers: zod_1.z.number().int(),
});
/**
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE PERIOD'S MONEY — DERIVED, AND OVERRIDABLE.                          │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * `occMoneyDefault` in the mockup: a period's frame is its retainer line items' per-period share,
 * its revenue theirs plus whatever time-and-materials work was actually logged inside it. Right
 * almost always, and wrong exactly when a month was renegotiated — a quiet quarter at half the
 * retainer, a one-off uplift.
 *
 * ⚠️ **`overridden` is what the screen needs to draw the difference.** A figure somebody typed and
 * a figure the arithmetic produced look identical in a table, and only one of them will change when
 * a line item is re-priced.
 */
exports.occurrenceEconomicsSchema = zod_1.z.object({
    /** The occurrence's own start, which is what identifies it in a generated series. */
    startsOn: zod_1.z.string(),
    index: zod_1.z.number().int(),
    currencyCode: zod_1.z.string().nullable(),
    budget: zod_1.z.string(),
    revenue: zod_1.z.string(),
    cost: zod_1.z.string(),
    marginPct: zod_1.z.number().int().nullable(),
    /**
     * C57. ⚠️ **What was billed FOR this period — which is not the same set as the invoices whose
     * date falls inside it.** An invoice named at the occurrence counts against it wherever it was
     * dated; one against a line item of the same contract counts against the period its date lands
     * in, which is how a fixed deliverable billed mid-frame reaches the right month. `occInvoices` in
     * the mockup is exactly these two arms.
     */
    invoiced: zod_1.z.string(),
    remaining: zod_1.z.string(),
    /**
     * ⚠️ **Over-frame work, kept SEPARATE from the revenue above.** A `billable` line item neither
     * carries nor consumes the frame — *"kereten felüli munka: nem visz és nem fogyaszt keretet"* —
     * so folding it in would make a period look like it had earned its retainer when it had not.
     */
    overFrameRevenue: zod_1.z.string(),
    /** Hours logged inside the period, against every line item of the contract. */
    loggedHours: zod_1.z.string(),
    /** Hours planned inside it. */
    plannedHours: zod_1.z.string(),
    budgetOverridden: zod_1.z.boolean(),
    revenueOverridden: zod_1.z.boolean(),
    note: zod_1.z.string(),
});
/**
 * ⚠️ **Null CLEARS an override and returns the period to the arithmetic**; an absent field leaves
 * it. The two are different answers and a screen needs both — *"put it back"* is the commonest
 * thing somebody does after typing a figure they then think better of.
 */
exports.saveOccurrenceMoneyRequestSchema = zod_1.z.object({
    budget: zod_1.z.number().min(0).nullable().optional(),
    revenue: zod_1.z.number().min(0).nullable().optional(),
    note: zod_1.z.string().max(500).optional(),
});
exports.saveLineItemMoneyRequestSchema = zod_1.z.object({
    scope: zod_1.z.enum(exports.LINE_ITEM_SCOPES).optional(),
    /** ⚠️ U-5 · per period on a recurring contract, the whole agreement anywhere else — as stored. */
    budget: zod_1.z.number().min(0).nullable().optional(),
    revenue: zod_1.z.number().min(0).nullable().optional(),
    rate: zod_1.z.number().min(0).nullable().optional(),
    rateUnit: zod_1.z.enum(['hour', 'day']).nullable().optional(),
    currencyCode: zod_1.z
        .string()
        .regex(/^[A-Z]{3}$/)
        .nullable()
        .optional(),
    version: zod_1.z.number().int().optional(),
});
//# sourceMappingURL=economics.js.map